// SensorTrendTable Component
// Displays trend analysis results in a tabular format
// Shows rate of change and other metrics for each sensor
import React from 'react';
import { Card } from "@/components/ui/card";

// Props interface for the SensorTrendTable component
interface SensorTrendTableProps {
  results: Record<string, any>;  // Analysis results data
  rateOfChangeDenominator: string;  // Time unit for rate of change (e.g., "day", "hour")
}

export const SensorTrendTable: React.FC<SensorTrendTableProps> = ({ results, rateOfChangeDenominator }) => {
  return (
    <Card className="p-6">
      {/* Table header with title */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Sensor Rate of Change (%/{rateOfChangeDenominator})</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full border">
          {/* Table header with column names */}
          <thead>
            <tr className="bg-gray-50">
              <th className="border px-4 py-2 text-left">Base Sensor</th>
              <th className="border px-4 py-2 text-right">Min</th>
              <th className="border px-4 py-2 text-right">Max</th>
              <th className="border px-4 py-2 text-right">Mean</th>
              <th className="border px-4 py-2 text-right">Median</th>
              <th className="border px-4 py-2 text-right">Std Dev</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              // Group sensors by base name and extract statistics
              const groupedSensors = Object.entries(results.results).reduce((acc, [sensor, data]) => {
                const lastUnderscoreIndex = sensor.lastIndexOf('_');
                const baseSensor = sensor.substring(0, lastUnderscoreIndex);
                const stat = sensor.substring(lastUnderscoreIndex + 1);
                if (!acc[baseSensor]) {
                  acc[baseSensor] = {};
                }
                acc[baseSensor][stat] = data;
                return acc;
              }, {} as Record<string, Record<string, any>>);

              // Sort sensors by absolute rate of change of mean value
              return Object.entries(groupedSensors)
                .sort(([_, a], [__, b]) => 
                  Math.abs(b.mean?.rate_of_change || 0) - Math.abs(a.mean?.rate_of_change || 0)
                )
                .map(([baseSensor, stats]) => (
                  <tr key={baseSensor} className="hover:bg-gray-50">
                    {/* Base sensor name */}
                    <td className="border px-4 py-2">{baseSensor}</td>
                    {/* Minimum rate of change with color coding */}
                    <td className="border px-4 py-2 text-right">
                      <span className={stats.min?.rate_of_change > 0 ? 'text-green-600' : 'text-red-600'}>
                        {stats.min?.rate_of_change?.toFixed(2) || 'N/A'}%
                      </span>
                    </td>
                    {/* Maximum rate of change with color coding */}
                    <td className="border px-4 py-2 text-right">
                      <span className={stats.max?.rate_of_change > 0 ? 'text-green-600' : 'text-red-600'}>
                        {stats.max?.rate_of_change?.toFixed(2) || 'N/A'}%
                      </span>
                    </td>
                    {/* Mean rate of change with color coding */}
                    <td className="border px-4 py-2 text-right">
                      <span className={stats.mean?.rate_of_change > 0 ? 'text-green-600' : 'text-red-600'}>
                        {stats.mean?.rate_of_change?.toFixed(2) || 'N/A'}%
                      </span>
                    </td>
                    {/* Median rate of change with color coding */}
                    <td className="border px-4 py-2 text-right">
                      <span className={stats.median?.rate_of_change > 0 ? 'text-green-600' : 'text-red-600'}>
                        {stats.median?.rate_of_change?.toFixed(2) || 'N/A'}%
                      </span>
                    </td>
                    {/* Standard deviation rate of change with color coding */}
                    <td className="border px-4 py-2 text-right">
                      <span className={stats.std?.rate_of_change > 0 ? 'text-green-600' : 'text-red-600'}>
                        {stats.std?.rate_of_change?.toFixed(2) || 'N/A'}%
                      </span>
                    </td>
                  </tr>
                ));
            })()}
          </tbody>
        </table>
      </div>
    </Card>
  );
}; 